import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { Field } from 'redux-form';

import actions from 'actions';
import HabitationShortcut from 'components/HabitationShortcut';
import HabitationsList from 'components/HabitationsList';
import Input from 'components/Input/Default';
import Select from 'components/Input/Select';
import Label from 'components/Label';
import ClosableTooltip from 'components/Tooltip/Closable';
import { FORM_ADDRESS_NAME, FORM_CITY_NAME, FORM_ZIP } from 'constants/Global';
import { isValidAddress, isValidZip, onlyAddressFieldIsEmpty } from 'helpers/FormInformations';
import { deepEqual } from 'helpers/ObjectUtils';
import setRef from 'helpers/Ref';
import { translate } from 'helpers/Translate';

/**
 * Address fields component.
 */
export default class Address extends Component {
    /**
     * Creates an instance of Address.
     * @param {Object} props Given props.
     */
    constructor(props) {
        super(props);

        const fullAddress = {
            cityName: props.values[FORM_CITY_NAME],
            zip: props.values[FORM_ZIP],
            addressName: props.values[FORM_ADDRESS_NAME],
        };

        // Launch an address resolution if we prefill zip and city name
        if (fullAddress.cityName && fullAddress.zip) {
            props.dispatch(
                actions.getAddresses(
                    fullAddress.addressName,
                    fullAddress.cityName,
                    fullAddress.zip,
                ),
            );
        }

        // Get cities list if we prefill zip
        if (isValidZip(props.values[FORM_ZIP])) {
            props.dispatch(actions.getCitiesFromZip(props.values.zip));
        }

        // Save user address fields in store
        if (isValidAddress(fullAddress)) {
            props.dispatch(
                actions.updateSelectedAddress(
                    fullAddress.addressName,
                    fullAddress.cityName,
                    fullAddress.zip,
                ),
            );
        }

        this.handleTooltip = this.handleTooltip.bind(this);
    }

    /**
     * React lifecycle.
     * Handle tooltip display
     */
    componentDidMount() {
        document.addEventListener(
            'css-rule-loaded-.tooltip--estima-address-field',
            () => {
                this.handleTooltip(this.props);
            },
            false,
        );

        if (onlyAddressFieldIsEmpty(this.props.values)) {
            this.input.focus();
        }
    }

    /**
     * React lifecycle.
     * Update value displayed if real value has changed.
     * @param {Object} prevProps Prev props of component
     */
    componentDidUpdate(prevProps) {
        const { dispatch, values } = this.props;

        const prevFullAddress = {
            cityName: prevProps.values[FORM_CITY_NAME],
            zip: prevProps.values[FORM_ZIP],
            addressName: prevProps.values[FORM_ADDRESS_NAME],
        };

        const fullAddress = {
            cityName: values[FORM_CITY_NAME],
            zip: values[FORM_ZIP],
            addressName: values[FORM_ADDRESS_NAME],
        };

        if (!deepEqual(fullAddress, prevFullAddress)) {
            // Launch an address resolution to recenter the map
            dispatch(
                actions.getAddresses(
                    fullAddress.addressName,
                    fullAddress.cityName,
                    fullAddress.zip,
                ),
            );

            // Save user address fields in store
            if (isValidAddress(fullAddress)) {
                dispatch(
                    actions.updateSelectedAddress(
                        fullAddress.addressName,
                        fullAddress.cityName,
                        fullAddress.zip,
                    ),
                );
            }

            // Address becomes unvalid, disable step
            if (!isValidAddress(fullAddress)) {
                dispatch(actions.validStep(null, false));
            }
        }

        // Zip has change, reload city drop down
        if (values[FORM_ZIP] && values[FORM_ZIP] !== prevProps.values[FORM_ZIP]) {
            if (isValidZip(values[FORM_ZIP])) {
                dispatch(actions.getCitiesFromZip(values[FORM_ZIP]));
            }
        }

        if (global.loadedCssSelectors['.tooltip--estima-address-field']) {
            this.handleTooltip(this.props);
        }
    }

    /**
     * Function to handle tooltip state
     * @param {Object} props    Props of component
     */
    handleTooltip(props) {
        if (onlyAddressFieldIsEmpty(props.values)) {
            ReactTooltip.show(this.input);
        } else {
            ReactTooltip.hide(this.input);
        }
    }

    /**
     * React lifecycle.
     */
    render() {
        const { address, habitations, values, isEditing } = this.props;
        const shouldHideAddressField =
            !isValidZip(values[FORM_ZIP]) || values[FORM_CITY_NAME] === '';
        const addressBlockClasses = classNames({
            'row-container row-container--vertical row-container-group__item': true,
            'row-container--disabled': shouldHideAddressField,
        });

        return (
            <div data-step-address>
                {!isEditing && (
                    <div className="row-container-group">
                        <div className="row-container--vertical row-container-group__item">
                            <HabitationShortcut habitations={habitations.match} />
                        </div>
                    </div>
                )}
                <div className="row-container-group">
                    <div className="row-container row-container--vertical row-container--padding-bottom row-container-group__item">
                        <div className="row-container__label">
                            <Label labelFor={FORM_ZIP} text={translate('Code postal')} />
                        </div>
                        <div className="row-container__field">
                            <Field component={Input} name={FORM_ZIP} type="tel" />
                        </div>
                    </div>

                    <div className="row-container row-container--vertical row-container--padding-bottom row-container-group__item">
                        <div className="row-container__label">
                            <Label labelFor={FORM_CITY_NAME} text={translate('Ville')} />
                        </div>
                        <div className="row-container__field">
                            <Field
                                component={Select}
                                emptyOption={address.placesOptions.length > 1}
                                emptyLabel={address.placesOptions.length > 1 ? 'Choisir' : ''}
                                name={FORM_CITY_NAME}
                                type="text"
                                options={address.placesOptions}
                            />
                        </div>
                    </div>
                </div>
                <div className="row-container-group">
                    <div className={addressBlockClasses}>
                        <div className="row-container__label">
                            <Label labelFor={FORM_ADDRESS_NAME} text={translate('Adresse')} />
                        </div>
                        <div className="row-container__field">
                            <ClosableTooltip tooltipId="address">
                                <Field
                                    inputRef={(ref) => setRef(this, 'input', ref)}
                                    forwardRef
                                    data-for="address"
                                    data-tip={translate(
                                        "Renseignez l'adresse postale du bien pour l'estimer",
                                    )}
                                    data-event="none"
                                    component={Input}
                                    name={FORM_ADDRESS_NAME}
                                    disabled={shouldHideAddressField}
                                    fullWidth
                                    placeholder={translate('Ex : 10 rue du Château, Paris')}
                                />
                            </ClosableTooltip>
                        </div>
                    </div>
                </div>
                {!isEditing && (
                    <div className="row-container-group">
                        <div className="row-container--vertical row-container-group__item">
                            <HabitationsList habitations={habitations.list} />
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

Address.propTypes = {
    dispatch: PropTypes.func,
    values: PropTypes.shape({}),
};
